import * as React from "react";
import { useSearchParams } from "react-router-dom";
import { useMutation, useQuery } from "convex/react";
import { toast } from "sonner";
import { api } from "$convex/convexApi";
import { ArrowUp, ChevronRight, Hand, Headset, Inbox, Loader2, MessageSquare, Sparkles } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Conversation,
  ConversationContent,
  ConversationScrollButton,
  useConversationContext,
} from "@/components/ui/conversation";
import { MarkdownBody } from "@/components/ui/markdown-body";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { BADGE_TONES } from "@/lib/badge-tones";
import { cn } from "@/lib/utils";

type ThreadStatus =
  | "open"
  | "waiting_on_customer"
  | "waiting_on_internal"
  | "resolved"
  | "closed";
type ThreadListFilter = "open" | "resolved" | "closed";

type SupportThreadRow = {
  thread: {
    _id: string;
    customerId: string;
    agentId?: string;
    assignedInternalUserId?: string;
    priority?: "P1" | "P2" | "P3";
    status: ThreadStatus;
    closedAt?: number;
    resolvedAt?: number;
    firstResponseAt?: number;
    handoffRequestedAt?: number;
    humanTakenOver?: boolean;
    lastMessageAt: number;
    lastCustomerMessageAt?: number;
    lastMessagePreview?: string;
    createdAt: number;
    updatedAt: number;
  };
  customer:
    | {
        _id: string;
        name?: string | null;
        email?: string | null;
        company?: string | null;
      }
    | null
    | undefined;
};

type SupportMessage = {
  _id: string;
  senderType: "customer" | "internal";
  senderSubType?: "customer" | "human" | "agent";
  visibility?: "public" | "internal";
  body: string;
  createdAt: number;
};

type SupportThreadWithMessages = {
  thread: SupportThreadRow["thread"];
  customer: SupportThreadRow["customer"];
  messages: SupportMessage[];
};

type SupportAgent = {
  _id: string;
  name: string;
  enabled: boolean;
  autoReplyEnabled: boolean;
  mode: "auto_ack" | "openrouter";
};

function formatThreadTitle(customer: SupportThreadRow["customer"]): string {
  const company = customer?.company?.trim();
  const name = customer?.name?.trim();
  const email = customer?.email?.trim();
  return company || name || email || "Unknown customer";
}

function getInitials(customer: SupportThreadRow["customer"]): string {
  const raw =
    customer?.company?.trim() ||
    customer?.name?.trim() ||
    customer?.email?.trim() ||
    "";
  if (!raw) return "?";
  const parts = raw.split(/\s+/g).filter(Boolean);
  const first = parts[0]?.[0] ?? raw[0] ?? "?";
  const second = parts.length > 1 ? parts[1]?.[0] : raw[1];
  return (first + (second ?? "")).toUpperCase();
}

function formatShortTimestamp(ms: number): string {
  const d = new Date(ms);
  const now = new Date();
  const isSameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  if (isSameDay) {
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  }
  return d.toLocaleDateString([], { month: "short", day: "numeric" });
}

function hasPendingCustomerMessage(thread: SupportThreadRow["thread"]): boolean {
  return thread.status === "waiting_on_internal";
}

function isActiveThreadStatus(status: ThreadStatus): boolean {
  return (
    status === "open" ||
    status === "waiting_on_internal" ||
    status === "waiting_on_customer"
  );
}

const THREAD_STATUS_LABEL: Record<ThreadStatus, string> = {
  open: "Open",
  waiting_on_internal: "Waiting on internal",
  waiting_on_customer: "Waiting on customer",
  resolved: "Resolved",
  closed: "Closed",
};

const THREAD_STATUS_BADGE_CLASS: Record<ThreadStatus, string> = {
  open: BADGE_TONES.sky,
  waiting_on_internal: BADGE_TONES.red,
  waiting_on_customer: BADGE_TONES.sky,
  resolved: BADGE_TONES.emerald,
  closed: BADGE_TONES.slate,
};

function NewMessagesBanner({
  threadId,
  lastMessageId,
}: {
  threadId: string;
  lastMessageId: string | null;
}) {
  const { isAtBottom, scrollToBottom } = useConversationContext();
  const [unseen, setUnseen] = React.useState(0);
  const prevLastIdRef = React.useRef<string | null>(null);

  React.useEffect(() => {
    prevLastIdRef.current = null;
    setUnseen(0);
  }, [threadId]);

  React.useEffect(() => {
    if (!lastMessageId) return;
    if (prevLastIdRef.current === null) {
      prevLastIdRef.current = lastMessageId;
      return;
    }
    if (prevLastIdRef.current === lastMessageId) return;
    prevLastIdRef.current = lastMessageId;

    if (isAtBottom) {
      setUnseen(0);
    } else {
      setUnseen((n) => Math.min(n + 1, 99));
    }
  }, [lastMessageId, isAtBottom]);

  React.useEffect(() => {
    if (isAtBottom) setUnseen(0);
  }, [isAtBottom]);

  if (isAtBottom || unseen <= 0) return null;

  return (
    <div className="pointer-events-none absolute bottom-4 left-1/2 z-10 -translate-x-1/2">
      <Button
        type="button"
        variant="outline"
        className="pointer-events-auto rounded-full bg-background/95 backdrop-blur"
        onClick={() => {
          setUnseen(0);
          scrollToBottom("smooth");
        }}
      >
        <span className="mr-2 inline-flex size-2 rounded-full bg-red-500" />
        {unseen} new {unseen === 1 ? "message" : "messages"}
      </Button>
    </div>
  );
}

function SupportEmptyState({
  icon: Icon,
  title,
  description,
  children,
}: {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  description: string;
  children?: React.ReactNode;
}) {
  return (
    <div className="flex h-full items-center justify-center p-6">
      <div className="w-full max-w-sm rounded-2xl border bg-background/70 p-6 text-center backdrop-blur">
        <div className="mx-auto mb-3 flex size-12 items-center justify-center rounded-2xl border bg-muted/30">
          <Icon className="size-6 text-muted-foreground" />
        </div>
        <p className="text-sm font-semibold">{title}</p>
        <p className="mt-1 text-sm text-muted-foreground">{description}</p>
        {children ? <div className="mt-4 flex justify-center gap-2">{children}</div> : null}
      </div>
    </div>
  );
}

function InboxListSkeleton({ count = 8 }: { count?: number }) {
  return (
    <div className="space-y-1 p-1" aria-hidden="true">
      {Array.from({ length: count }).map((_, idx) => (
        <div key={idx} className="flex w-full items-start gap-3 rounded-lg px-3 py-2.5">
          <Skeleton className="mt-0.5 size-8 rounded-md" />
          <div className="min-w-0 flex-1 space-y-2">
            <div className="flex items-center justify-between gap-3">
              <Skeleton className="h-4 w-40 max-w-[70%]" />
              <Skeleton className="h-3 w-10" />
            </div>
            <Skeleton className="h-3 w-52 max-w-full" />
            <Skeleton className="h-3 w-64 max-w-full" />
          </div>
        </div>
      ))}
    </div>
  );
}

function ConversationSkeleton() {
  return (
    <div className="space-y-3 p-4" aria-hidden="true">
      <div className="flex items-end gap-2">
        <Skeleton className="size-8 rounded-full" />
        <Skeleton className="h-10 w-[260px] max-w-[70%] rounded-2xl" />
      </div>
      <div className="flex items-end justify-end gap-2">
        <Skeleton className="h-9 w-[220px] max-w-[70%] rounded-2xl" />
        <Skeleton className="size-8 rounded-full" />
      </div>
      <div className="flex items-end gap-2">
        <Skeleton className="size-8 rounded-full" />
        <div className="space-y-2">
          <Skeleton className="h-10 w-[320px] max-w-[70vw] rounded-2xl" />
          <Skeleton className="h-10 w-[240px] max-w-[60vw] rounded-2xl" />
        </div>
      </div>
      <div className="flex items-end justify-end gap-2">
        <Skeleton className="h-10 w-[300px] max-w-[70vw] rounded-2xl" />
        <Skeleton className="size-8 rounded-full" />
      </div>
      <div className="flex items-end gap-2">
        <Skeleton className="size-8 rounded-full" />
        <Skeleton className="h-9 w-[200px] max-w-[60vw] rounded-2xl" />
      </div>
    </div>
  );
}

export default function SupportPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedThreadId = searchParams.get("threadId") ?? "";

  const [status, setStatus] = React.useState<ThreadListFilter>("open");

  const threads = useQuery(api.support.listThreads, {
    status,
    limit: 100,
  }) as SupportThreadRow[] | undefined | null;

  const selectedCustomerId = React.useMemo(() => {
    if (!threads || !selectedThreadId) return null;
    const row = threads.find((r) => r.thread._id === selectedThreadId);
    return row?.thread.customerId ?? null;
  }, [threads, selectedThreadId]);

  const closedGroups = React.useMemo(() => {
    if (!threads || status !== "closed") return [];

    const byCustomer = new Map<
      string,
      {
        customerId: string;
        customer: SupportThreadRow["customer"];
        threads: SupportThreadRow["thread"][];
      }
    >();

    // Preserve the global sort order: `threads` is newest-first, so the first
    // time we see a customer becomes the "latest" thread for that group.
    for (const row of threads) {
      const customerId = row.thread.customerId;
      const existing = byCustomer.get(customerId);
      if (!existing) {
        byCustomer.set(customerId, {
          customerId,
          customer: row.customer,
          threads: [row.thread],
        });
        continue;
      }

      // Prefer a non-null customer object when available.
      if (!existing.customer && row.customer) {
        existing.customer = row.customer;
      }
      existing.threads.push(row.thread);
    }

    return Array.from(byCustomer.values());
  }, [threads, status]);

  const [closedExpandedCustomers, setClosedExpandedCustomers] = React.useState<Record<string, boolean>>({});

  // Keep customer groups collapsed by default; only expand on explicit user action.

  React.useEffect(() => {
    if (!threads || threads.length === 0) return;
    if (selectedThreadId) return;
    setSearchParams({ threadId: threads[0]!.thread._id }, { replace: true });
  }, [threads, selectedThreadId, setSearchParams]);

  const threadData = useQuery(
    api.support.getThreadWithMessages,
    selectedThreadId ? { threadId: selectedThreadId as any, limit: 300 } : "skip",
  ) as SupportThreadWithMessages | undefined | null;

  const agents = useQuery(api.support.listSupportAgents, {}) as SupportAgent[] | undefined | null;

  const sendInternalMessage = useMutation(api.support.sendInternalMessage);
  const setThreadStatus = useMutation(api.support.setThreadStatus);
  const setThreadAgent = useMutation(api.support.setThreadAgent);
  const takeOverThread = useMutation(api.support.takeOverThread);
  const releaseThreadToAgent = useMutation(api.support.releaseThreadToAgent);

  const [draft, setDraft] = React.useState("");
  const [isSending, setIsSending] = React.useState(false);
  const [threadAction, setThreadAction] = React.useState<
    null | "take_over" | "release" | "resolve" | "close" | "reopen"
  >(null);
  const [composerMode, setComposerMode] = React.useState<"public" | "internal">("public");

  React.useEffect(() => {
    setComposerMode("public");
  }, [selectedThreadId]);

  const handleSelectThread = React.useCallback(
    (threadId: string) => {
      setSearchParams({ threadId });
    },
    [setSearchParams],
  );

  const handleStatusChange = React.useCallback(
    (next: ThreadListFilter) => {
      setStatus(next);
      setSearchParams({}, { replace: true });
    },
    [setSearchParams],
  );

  const send = React.useCallback(async () => {
    const body = draft.trim();
    if (!selectedThreadId || !body || isSending) return;

    setIsSending(true);
    try {
      await sendInternalMessage({
        threadId: selectedThreadId as any,
        body,
        visibility: composerMode,
      });
      setDraft("");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to send message");
    } finally {
      setIsSending(false);
    }
  }, [draft, selectedThreadId, isSending, sendInternalMessage, composerMode]);

  const setThreadLifecycleStatus = React.useCallback(
    async (nextStatus: ThreadStatus, successMessage: string) => {
      if (!selectedThreadId) return;
      try {
        await setThreadStatus({
          threadId: selectedThreadId as any,
          status: nextStatus,
        });
        toast.success(successMessage);
        if (
          (status === "open" && nextStatus !== "open" && nextStatus !== "waiting_on_internal" && nextStatus !== "waiting_on_customer") ||
          (status === "resolved" && nextStatus !== "resolved") ||
          (status === "closed" && nextStatus !== "closed")
        ) {
          setSearchParams({}, { replace: true });
        }
      } catch (err) {
        toast.error(err instanceof Error ? err.message : "Failed to update thread");
      }
    },
    [selectedThreadId, setThreadStatus, status, setSearchParams],
  );

  const toggleThreadStatus = React.useCallback(async () => {
    if (!threadData?.thread || threadAction !== null) return;
    const nextStatus: ThreadStatus = isActiveThreadStatus(threadData.thread.status)
      ? "resolved"
      : "open";
    setThreadAction(nextStatus === "resolved" ? "resolve" : "reopen");
    try {
      await setThreadLifecycleStatus(
        nextStatus,
        nextStatus === "resolved" ? "Thread resolved" : "Thread reopened",
      );
    } finally {
      setThreadAction(null);
    }
  }, [threadData?.thread, setThreadLifecycleStatus, threadAction]);

  const closeResolvedThread = React.useCallback(async () => {
    if (!threadData?.thread || threadData.thread.status !== "resolved" || threadAction !== null) return;
    setThreadAction("close");
    try {
      await setThreadLifecycleStatus("closed", "Thread closed");
    } finally {
      setThreadAction(null);
    }
  }, [threadData?.thread, setThreadLifecycleStatus, threadAction]);

  const setAgent = React.useCallback(
    async (agentId: string) => {
      if (!selectedThreadId) return;
      try {
        await setThreadAgent({
          threadId: selectedThreadId as any,
          agentId: agentId === "__none__" ? undefined : (agentId as any),
        });
        toast.success("Agent updated");
      } catch (err) {
        toast.error(err instanceof Error ? err.message : "Failed to update agent");
      }
    },
    [selectedThreadId, setThreadAgent],
  );

  const handleTakeOver = React.useCallback(async () => {
    if (!selectedThreadId || threadAction !== null) return;
    setThreadAction("take_over");
    try {
      await takeOverThread({ threadId: selectedThreadId as any });
      toast.success("You're now in control of this thread");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to take over");
    } finally {
      setThreadAction(null);
    }
  }, [selectedThreadId, takeOverThread, threadAction]);

  const handleRelease = React.useCallback(async () => {
    if (!selectedThreadId || threadAction !== null) return;
    setThreadAction("release");
    try {
      await releaseThreadToAgent({ threadId: selectedThreadId as any });
      toast.success("Released to AI agent");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to release");
    } finally {
      setThreadAction(null);
    }
  }, [selectedThreadId, releaseThreadToAgent, threadAction]);

  const isThreadLoading = Boolean(selectedThreadId) && threadData == null;
  const activeThreadTitle = !selectedThreadId
    ? "Select a thread"
    : isThreadLoading
      ? "Loading…"
      : formatThreadTitle(threadData?.customer);
  const activeThreadEmail =
    !selectedThreadId || isThreadLoading
      ? null
      : threadData?.customer?.email ?? null;
  const activeThreadPending = threadData?.thread ? hasPendingCustomerMessage(threadData.thread) : false;
  const activeThreadClosed =
    threadData?.thread?.status === "closed" || threadData?.thread?.status === "resolved";
  const isThreadActionPending = threadAction !== null;

  const inboxSubtitle = React.useMemo(() => {
    if (threads == null) return "Loading…";
    if (status === "closed") {
      const customers = closedGroups.length;
      const threadCount = threads.length;
      return `${customers} ${customers === 1 ? "customer" : "customers"} • ${threadCount} ${threadCount === 1 ? "thread" : "threads"}`;
    }
    return `${threads.length} ${threads.length === 1 ? "thread" : "threads"}`;
  }, [threads, status, closedGroups.length]);

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Support</h1>
          <p className="text-muted-foreground mt-1">Customer chats from web and apps</p>
        </div>
        <Tabs value={status} onValueChange={(value) => handleStatusChange(value as ThreadListFilter)}>
          <TabsList>
            <TabsTrigger value="open">Open</TabsTrigger>
            <TabsTrigger value="resolved">Resolved</TabsTrigger>
            <TabsTrigger value="closed">Closed</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      <div className="overflow-hidden rounded-2xl border bg-card">
        <div className="grid h-[72vh] lg:grid-cols-[360px_1fr]">
          {/* Inbox */}
          <section className="min-h-0 border-b bg-card lg:border-b-0 lg:border-r">
            <div className="flex items-center justify-between gap-3 border-b bg-background/60 px-4 py-3 backdrop-blur">
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold">Inbox</p>
                <p className="truncate text-xs text-muted-foreground">{inboxSubtitle}</p>
              </div>
            </div>

            <div className="max-h-[calc(72vh-49px)] overflow-y-auto p-2">
              {threads == null ? (
                <InboxListSkeleton />
              ) : threads.length === 0 ? (
                <div className="flex flex-col items-center justify-center gap-3 px-6 py-14 text-center">
                  <div className="flex size-12 items-center justify-center rounded-2xl border bg-muted/30">
                    <Inbox className="size-6 text-muted-foreground" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold">No {status} threads</p>
                      <p className="mt-1 text-sm text-muted-foreground">
                      {status === "open"
                        ? "When customers message in, they'll show up here."
                        : status === "resolved"
                          ? "Resolved conversations will appear here once you've handled them."
                          : "Closed conversations will appear here once they've ended."}
                    </p>
                  </div>
                </div>
              ) : status === "closed" ? (
                <div className="space-y-2">
                  {closedGroups.map((group) => {
                    const latest = group.threads[0];
                    if (!latest) return null;

                    const expanded = Boolean(closedExpandedCustomers[group.customerId]);
                    const isGroupActive = selectedCustomerId === group.customerId;
                    const title = formatThreadTitle(group.customer);
                    const initials = getInitials(group.customer);
                    const email = group.customer?.email?.trim() || null;
                    const preview = latest.lastMessagePreview ?? "No messages yet.";
                    const groupTimestamp = latest.closedAt ?? latest.lastMessageAt;

                    return (
                      <div
                        key={group.customerId}
                        className={cn(
                          "rounded-xl border bg-background/40",
                          isGroupActive ? "border-border" : "border-transparent",
                        )}
                      >
                        <button
                          type="button"
                          className={cn(
                            "group flex w-full items-start gap-3 rounded-xl px-3 py-2.5 text-left transition-colors",
                            expanded ? "bg-muted/30" : "hover:bg-muted/40",
                          )}
                          onClick={() => {
                            setClosedExpandedCustomers((prev) => ({
                              ...prev,
                              [group.customerId]: !prev[group.customerId],
                            }));
                            if (!isGroupActive) handleSelectThread(latest._id);
                          }}
                        >
                          <div className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-md border bg-background text-[11px] font-semibold text-muted-foreground">
                            {initials}
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-start justify-between gap-3">
                              <p className="truncate text-sm font-semibold">{title}</p>
                              <span className="shrink-0 text-xs text-muted-foreground">
                                {formatShortTimestamp(groupTimestamp)}
                              </span>
                            </div>
                            {email ? (
                              <p className="mt-0.5 truncate text-xs text-muted-foreground">{email}</p>
                            ) : null}
                            <div className="mt-1 flex items-start justify-between gap-3">
                              <p className="line-clamp-2 text-xs text-muted-foreground">{preview}</p>
                              <Badge
                                variant="secondary"
                                className="mt-0.5 shrink-0 rounded-full px-2 py-0 text-[10px] tabular-nums"
                                title={`${group.threads.length} closed threads`}
                              >
                                {group.threads.length}
                              </Badge>
                            </div>
                          </div>

                          <ChevronRight
                            className={cn(
                              "mt-1 size-4 shrink-0 text-muted-foreground transition-transform duration-200",
                              expanded && "rotate-90",
                            )}
                          />
                        </button>

                        {expanded ? (
                          <div className="space-y-1 pb-2 pl-12 pr-2">
                            {group.threads.map((t) => {
                              const isActive = t._id === selectedThreadId;
                              const ts = t.closedAt ?? t.lastMessageAt;
                              const tPreview = t.lastMessagePreview ?? "No messages yet.";
                              return (
                                <button
                                  key={t._id}
                                  type="button"
                                  className={cn(
                                    "w-full rounded-lg border border-transparent px-3 py-2 text-left transition-colors",
                                    isActive ? "bg-muted" : "hover:bg-muted/40",
                                  )}
                                  onClick={() => handleSelectThread(t._id)}
                                >
                                  <div className="flex items-start justify-between gap-3">
                                    <p className="truncate text-xs font-medium text-foreground">
                                      Closed thread
                                    </p>
                                    <span className="shrink-0 text-[11px] text-muted-foreground">
                                      {formatShortTimestamp(ts)}
                                    </span>
                                  </div>
                                  <p className="mt-0.5 line-clamp-2 text-[11px] text-muted-foreground">
                                    {tPreview}
                                  </p>
                                </button>
                              );
                            })}
                          </div>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="space-y-1">
                  {threads.map((row) => {
                    const isActive = row.thread._id === selectedThreadId;
                    const title = formatThreadTitle(row.customer);
                    const initials = getInitials(row.customer);
                    const preview = row.thread.lastMessagePreview ?? "No messages yet.";
                    const pending = hasPendingCustomerMessage(row.thread);

                    return (
                      <button
                        key={row.thread._id}
                        type="button"
                        className={cn(
                          "group flex w-full items-start gap-3 rounded-lg px-3 py-2.5 text-left transition-colors",
                          isActive ? "bg-muted" : "hover:bg-muted/40",
                        )}
                        onClick={() => handleSelectThread(row.thread._id)}
                      >
                        <div className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-md border bg-background text-[11px] font-semibold text-muted-foreground">
                          {initials}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex min-w-0 items-center gap-2">
                              {pending ? (
                                <span
                                  className="mt-0.5 inline-flex size-2 shrink-0 rounded-full bg-red-500"
                                  aria-label="New customer message"
                                />
                              ) : null}
                              <p className={cn("truncate text-sm font-semibold", pending && "text-foreground")}>
                                {title}
                              </p>
                            </div>
                            <span className={cn("shrink-0 text-xs", pending ? "text-foreground" : "text-muted-foreground")}>
                              {formatShortTimestamp(row.thread.lastMessageAt)}
                            </span>
                          </div>
                          {row.customer?.email ? (
                            <p className="mt-0.5 truncate text-xs text-muted-foreground">
                              {row.customer.email}
                            </p>
                          ) : null}
                          <p className={cn("mt-1 line-clamp-2 text-xs", pending ? "text-foreground/90" : "text-muted-foreground")}>
                            {preview}
                          </p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </section>

          {/* Conversation */}
          <section className="flex min-h-0 flex-col">
            <header className="flex items-start justify-between gap-3 border-b bg-background/60 px-4 py-3 backdrop-blur">
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold">{activeThreadTitle}</p>
                <p className="truncate text-xs text-muted-foreground">
                  {!selectedThreadId
                    ? "Select a thread"
                    : isThreadLoading
                      ? "Loading…"
                      : activeThreadEmail || "Customer email not available"}
                </p>
              </div>
              {threadData?.thread ? (
                <div className="flex items-center gap-2">
                  <Badge
                    variant="outline"
                    className={cn(
                      THREAD_STATUS_BADGE_CLASS[threadData.thread.status],
                      "gap-1.5 px-3 py-1",
                    )}
                  >
                    {THREAD_STATUS_LABEL[threadData.thread.status]}
                  </Badge>

                  {isActiveThreadStatus(threadData.thread.status) ? (
                    <>
                      {activeThreadPending ? (
                        <Badge variant="outline" className={cn(BADGE_TONES.red, "gap-1.5 px-3 py-1")}>
                          New message
                        </Badge>
                      ) : null}

                      {threadData.thread.humanTakenOver ? (
                        <>
                          <Badge
                            variant="outline"
                            className={cn(BADGE_TONES.amber, "gap-1.5 px-3 py-1")}
                          >
                            <Hand className="size-3" />
                            You're in control
                          </Badge>
                          <Button variant="outline" size="sm" disabled={isThreadActionPending} onClick={() => void handleRelease()}>
                            {threadAction === "release" ? "Releasing..." : "Release to AI"}
                          </Button>
                        </>
                      ) : (
                        <>
                          <Select
                            value={(threadData.thread.agentId as string | undefined) ?? "__none__"}
                            onValueChange={(value) => void setAgent(value)}
                          >
                            <SelectTrigger className="h-9 w-[180px]">
                              <SelectValue placeholder="Agent" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="__none__">Unassigned</SelectItem>
                              {(agents ?? []).map((a) => (
                                <SelectItem key={a._id} value={a._id}>
                                  {a.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Button variant="outline" size="sm" disabled={isThreadActionPending} onClick={() => void handleTakeOver()}>
                            <Hand className="mr-1.5 size-3.5" />
                            {threadAction === "take_over" ? "Taking..." : "Take Control"}
                          </Button>
                        </>
                      )}

                      <Button variant="outline" disabled={isThreadActionPending} onClick={() => void toggleThreadStatus()}>
                        {threadAction === "resolve" ? "Resolving..." : "Resolve"}
                      </Button>
                    </>
                  ) : threadData.thread.status === "resolved" ? (
                    <>
                      <Button variant="outline" disabled={isThreadActionPending} onClick={() => void closeResolvedThread()}>
                        {threadAction === "close" ? "Closing..." : "Close"}
                      </Button>
                      <Button variant="outline" disabled={isThreadActionPending} onClick={() => void toggleThreadStatus()}>
                        {threadAction === "reopen" ? "Reopening..." : "Reopen"}
                      </Button>
                    </>
                  ) : (
                    <Button variant="outline" disabled={isThreadActionPending} onClick={() => void toggleThreadStatus()}>
                      {threadAction === "reopen" ? "Reopening..." : "Reopen"}
                    </Button>
                  )}
                </div>
              ) : null}
            </header>

            <div
              className={cn(
                "min-h-0 flex-1 overflow-y-auto",
                activeThreadClosed
                  ? "bg-gradient-to-b from-red-50 via-red-50/20 to-background dark:from-red-950/20 dark:via-background"
                  : "bg-gradient-to-b from-muted/30 via-muted/10 to-background",
              )}
            >
              {!selectedThreadId ? (
                <SupportEmptyState
                  icon={MessageSquare}
                  title="Select a thread"
                  description="Choose a conversation from the inbox to view messages and reply."
                />
              ) : isThreadLoading ? (
                <ConversationSkeleton />
              ) : !threadData?.thread ? (
                <SupportEmptyState
                  icon={MessageSquare}
                  title="Thread unavailable"
                  description="This conversation couldn't be loaded. It may have been deleted or you may not have access."
                >
                  <Button variant="outline" onClick={() => setSearchParams({}, { replace: true })}>
                    Back to inbox
                  </Button>
                </SupportEmptyState>
              ) : threadData.messages.length === 0 ? (
                <SupportEmptyState
                  icon={MessageSquare}
                  title="No messages yet"
                  description="Send the first reply below to start the conversation."
                />
              ) : (
                <Conversation className="h-full">
                  <ConversationContent className="p-4">
                    {threadData.thread.status === "closed" || threadData.thread.status === "resolved" ? (
                      <div className="mb-4 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-950 dark:border-red-800/60 dark:bg-red-950/20 dark:text-red-50/90">
                        <p className="font-medium">
                          {threadData.thread.status === "resolved" ? "Chat resolved" : "Chat closed"}
                        </p>
                        <p className="mt-1 text-xs text-red-800/80 dark:text-red-200/80">
                          {threadData.thread.status === "resolved"
                            ? typeof threadData.thread.resolvedAt === "number"
                              ? `Resolved on ${formatShortTimestamp(threadData.thread.resolvedAt)}.`
                              : "This chat was marked as resolved."
                            : typeof threadData.thread.closedAt === "number"
                              ? `Closed on ${formatShortTimestamp(threadData.thread.closedAt)}.`
                              : "This chat was closed by the customer."}
                        </p>
                      </div>
                    ) : null}
                    {threadData.messages.map((m, idx) => {
                      const mine = m.senderType === "internal";
                      const isAgent = m.senderType === "internal" && m.senderSubType === "agent";
                      const isHuman = m.senderType === "internal" && m.senderSubType === "human";
                      const isInternalNote = m.visibility === "internal";
                      const prev = idx > 0 ? threadData.messages[idx - 1] : null;
                      const next = idx < threadData.messages.length - 1 ? threadData.messages[idx + 1] : null;
                      const isPrevSame =
                        prev?.senderType === m.senderType &&
                        prev?.senderSubType === m.senderSubType;
                      const isLastInGroup =
                        next?.senderType !== m.senderType ||
                        next?.senderSubType !== m.senderSubType;

                      const customerInitials = getInitials(threadData.customer);
                      const InternalIcon = isAgent ? Sparkles : Headset;

                      return (
                        <div
                          key={m._id}
                          className={cn(
                            "flex w-full items-end gap-2",
                            mine ? "justify-end" : "justify-start",
                            isPrevSame ? "mt-1" : "mt-3",
                          )}
                        >
                          {!mine ? (
                            <div className="mb-0.5 flex size-8 shrink-0 items-center justify-center rounded-full border bg-background text-[11px] font-semibold text-muted-foreground">
                              {customerInitials}
                            </div>
                          ) : null}

                          <div className={cn("flex min-w-0 flex-1 flex-col", mine && "items-end")}>
                            <div
                              className={cn(
                                "relative w-fit max-w-[85%] whitespace-pre-wrap break-words rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed sm:max-w-[520px]",
                                isLastInGroup && "pr-12",
                                mine
                                  ? isInternalNote
                                    ? "border border-amber-300 bg-amber-50 text-amber-950 dark:border-amber-700 dark:bg-amber-950/30 dark:text-amber-100"
                                    : "bg-neutral-100 text-foreground dark:bg-neutral-800"
                                  : isAgent
                                    ? "bg-card text-foreground border border-dashed"
                                    : "bg-card text-foreground border",
                              )}
                            >
                              {isInternalNote ? (
                                <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-amber-700 dark:text-amber-300">
                                  Internal note
                                </p>
                              ) : null}
                              {isAgent ? <MarkdownBody content={m.body} /> : m.body}
                              {isLastInGroup ? (
                                <div
                                  className={cn(
                                    "pointer-events-none absolute bottom-2 text-[10px] leading-none text-muted-foreground",
                                    mine ? "right-3 text-right" : "right-3",
                                  )}
                                >
                                  {formatShortTimestamp(m.createdAt)}
                                </div>
                              ) : null}
                            </div>
                          </div>

                          {mine ? (
                            <div className="mb-0.5 flex size-8 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground">
                              <InternalIcon className="size-4" />
                            </div>
                          ) : null}
                        </div>
                      );
                    })}
                  </ConversationContent>
                  <ConversationScrollButton />
                  <NewMessagesBanner
                    threadId={threadData.thread._id}
                    lastMessageId={threadData.messages.length ? threadData.messages[threadData.messages.length - 1]!._id : null}
                  />
                </Conversation>
              )}
            </div>

            <div className="border-t bg-card p-3">
              {threadData?.thread?.status === "closed" ||
              threadData?.thread?.status === "resolved" ? (
                <div className="rounded-2xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-950 dark:border-red-800/60 dark:bg-red-950/20 dark:text-red-50/90">
                  <div className="flex items-center justify-between gap-3">
                    <p className="min-w-0 truncate font-medium">
                      This chat is{" "}
                      {threadData?.thread?.status === "resolved" ? "resolved" : "closed"}
                    </p>
                    <Button variant="outline" size="sm" disabled={isThreadActionPending} onClick={() => void toggleThreadStatus()}>
                      {threadAction === "reopen" ? "Reopening..." : "Reopen to reply"}
                    </Button>
                  </div>
                  <p className="mt-0.5 text-xs text-red-800/80 dark:text-red-200/80">
                    Resolved and closed threads are read-only until reopened.
                  </p>
                </div>
              ) : (
                <>
                  <div className="mb-2 flex items-center gap-2">
                    <Button
                      type="button"
                      size="sm"
                      variant={composerMode === "public" ? "default" : "outline"}
                      className="h-7 rounded-lg px-3 text-xs"
                      onClick={() => setComposerMode("public")}
                    >
                      Reply to customer
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant={composerMode === "internal" ? "default" : "outline"}
                      className="h-7 rounded-lg px-3 text-xs"
                      onClick={() => setComposerMode("internal")}
                    >
                      Internal note
                    </Button>
                  </div>
                  <div className="flex items-end gap-2 rounded-2xl border bg-background p-2 focus-within:ring-2 focus-within:ring-ring/40">
                    <Textarea
                      value={draft}
                      onChange={(e) => setDraft(e.target.value)}
                      placeholder={
                        threadData?.thread
                          ? composerMode === "internal"
                            ? "Add an internal note…"
                            : "Reply to customer…"
                          : "Select a thread to reply…"
                      }
                      rows={3}
                      disabled={!threadData?.thread}
                      className="min-h-0 resize-none border-0 bg-transparent shadow-none focus-visible:border-0 focus-visible:ring-0"
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                          e.preventDefault();
                          void send();
                        }
                      }}
                    />
                    <Button
                      onClick={() => void send()}
                      disabled={!threadData?.thread || !draft.trim() || isSending}
                      size="icon"
                      className="shrink-0 rounded-xl"
                      aria-label="Send message"
                    >
                      {isSending ? <Loader2 className="size-4 animate-spin" /> : <ArrowUp />}
                    </Button>
                  </div>
                  <p className="mt-2 text-[11px] text-muted-foreground">
                    Press Enter to send, Shift+Enter for a new line.
                  </p>
                </>
              )}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
